import React, { useEffect, useMemo, useRef } from 'react';
import { View } from 'react-native';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { getDefaultPhoneCountryCode } from '@utils/phoneDefaultCountry';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import type { WhatsAppPhoneFieldProps } from './WhatsAppPhoneField.types';

const STYLE_ID = 'whatsapp-phone-field-dropdown-theme-v10';
const DROPDOWN_Z = '2147483646';

/**
 * Styles target .wa-phone-container / .wa-phone-dropdown — classes PhoneInput
 * actually puts on the DOM. (RN View className is unreliable on web.)
 *
 * material.css (loaded by schema PhoneField) uses absolute flags + padding-left:46px;
 * we keep that model so flags don't sit on country names.
 */
const THEME_CSS = `
.wa-phone-field {
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
  align-self: stretch !important;
  display: block !important;
  box-sizing: border-box !important;
  position: relative !important;
}
.wa-phone-field:has(.flag-dropdown.open) {
  z-index: ${DROPDOWN_Z} !important;
  overflow: visible !important;
}

.wa-phone-container.react-tel-input {
  position: relative !important;
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
  display: block !important;
  overflow: visible !important;
  box-sizing: border-box !important;
}
.wa-phone-container .flag-dropdown {
  position: absolute !important;
  left: 0 !important;
  top: 0 !important;
  bottom: 0 !important;
  width: 52px !important;
  max-width: 52px !important;
  background: transparent !important;
  border: none !important;
  border-right: 1px solid rgba(148, 163, 184, 0.35) !important;
  border-radius: 12px 0 0 12px !important;
  z-index: 5 !important;
  overflow: visible !important;
}
.wa-phone-container .flag-dropdown.open {
  z-index: ${DROPDOWN_Z} !important;
  overflow: visible !important;
}
.wa-phone-container .selected-flag,
.wa-phone-container .selected-flag:hover,
.wa-phone-container .selected-flag:focus {
  width: 52px !important;
  max-width: 52px !important;
  height: 100% !important;
  padding: 0 0 0 8px !important;
  display: block !important;
  background: transparent !important;
  border-radius: 12px 0 0 12px !important;
  box-sizing: border-box !important;
  position: relative !important;
}
.wa-phone-container .selected-flag .flag {
  position: absolute !important;
  top: 50% !important;
  margin-top: -5px !important;
  left: 8px !important;
  width: 16px !important;
  height: 11px !important;
  overflow: hidden !important;
  display: block !important;
  background-size: auto !important;
  background-repeat: no-repeat !important;
}
.wa-phone-container .selected-flag .arrow {
  position: relative !important;
  top: 50% !important;
  margin-top: -2px !important;
  left: 28px !important;
  width: 0 !important;
  height: 0 !important;
  border-left: 3px solid transparent !important;
  border-right: 3px solid transparent !important;
  border-top: 4px solid #64748b !important;
  border-bottom: none !important;
}
.wa-phone-container .selected-flag .arrow.up {
  border-top: none !important;
  border-bottom: 4px solid #64748b !important;
}
.wa-phone-field--dark .wa-phone-container .selected-flag .arrow {
  border-top-color: #94a3b8 !important;
}
.wa-phone-field--dark .wa-phone-container .selected-flag .arrow.up {
  border-bottom-color: #94a3b8 !important;
  border-top-color: transparent !important;
}
.wa-phone-container input.form-control,
.wa-phone-container input[type="tel"],
.wa-phone-container .wa-phone-input {
  width: 100% !important;
  min-width: 100% !important;
  max-width: 100% !important;
  height: 50px !important;
  padding-left: 64px !important;
  margin-left: 0 !important;
  background: transparent !important;
  border: none !important;
  outline: none !important;
  box-sizing: border-box !important;
}

/* —— Country list (class is on the <ul>; do not nest under .wa-phone-field only) —— */
.country-list.wa-phone-dropdown {
  display: block !important;
  list-style: none !important;
  background-color: #ffffff !important;
  color: #0f172a !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18) !important;
  padding: 0 !important;
  margin: 0 !important;
  left: 0 !important;
  right: auto !important;
  top: calc(100% + 4px) !important;
  width: 320px !important;
  min-width: 280px !important;
  max-width: min(360px, 90vw) !important;
  max-height: 240px !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
  position: absolute !important;
  z-index: ${DROPDOWN_Z} !important;
  box-sizing: border-box !important;
  border-radius: 8px !important;
}
.country-list.wa-phone-dropdown.wa-phone-dropdown--dark {
  background-color: #1e293b !important;
  color: #e2e8f0 !important;
  border: 1px solid #374151 !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55) !important;
}

/* material.css: absolute flag @ left:13px — keep left padding so text clears the flag */
.country-list.wa-phone-dropdown .country {
  position: relative !important;
  display: block !important;
  list-style: none !important;
  float: none !important;
  clear: both !important;
  padding: 10px 14px 10px 46px !important;
  margin: 0 !important;
  min-height: 40px !important;
  height: auto !important;
  line-height: 20px !important;
  box-sizing: border-box !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  cursor: pointer !important;
  color: inherit !important;
  background: transparent !important;
}
.country-list.wa-phone-dropdown .country .flag {
  position: absolute !important;
  left: 13px !important;
  top: 50% !important;
  margin: -5.5px 0 0 0 !important;
  width: 16px !important;
  height: 11px !important;
  min-width: 16px !important;
  min-height: 11px !important;
  overflow: hidden !important;
  display: block !important;
  background-size: auto !important;
  background-repeat: no-repeat !important;
  pointer-events: none !important;
}
.country-list.wa-phone-dropdown .country .country-name {
  display: inline !important;
  margin: 0 6px 0 0 !important;
  padding: 0 !important;
  position: static !important;
  color: inherit !important;
}
.country-list.wa-phone-dropdown .country .dial-code {
  display: inline !important;
  margin: 0 !important;
  padding: 0 !important;
  position: static !important;
  color: #64748b !important;
}
.country-list.wa-phone-dropdown.wa-phone-dropdown--dark .country .dial-code {
  color: #94a3b8 !important;
}
.country-list.wa-phone-dropdown .country:hover,
.country-list.wa-phone-dropdown .country.highlight {
  background-color: #f1f5f9 !important;
}
.country-list.wa-phone-dropdown.wa-phone-dropdown--dark .country:hover,
.country-list.wa-phone-dropdown.wa-phone-dropdown--dark .country.highlight {
  background-color: #334155 !important;
}
.country-list.wa-phone-dropdown .divider {
  display: block !important;
  padding: 0 !important;
  margin: 4px 0 !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 0 !important;
  min-height: 0 !important;
}
.country-list.wa-phone-dropdown.wa-phone-dropdown--dark .divider {
  border-bottom-color: #475569 !important;
}

/* Search row — library applies searchClass to li, and searchClass-box / -emoji to children */
.country-list.wa-phone-dropdown .search {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 8px !important;
  list-style: none !important;
  position: sticky !important;
  top: 0 !important;
  z-index: 3 !important;
  padding: 10px !important;
  margin: 0 !important;
  height: auto !important;
  min-height: 52px !important;
  line-height: normal !important;
  box-sizing: border-box !important;
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  float: none !important;
}
.country-list.wa-phone-dropdown.wa-phone-dropdown--dark .search {
  background-color: #0f172a !important;
  border-bottom-color: #334155 !important;
}
.country-list.wa-phone-dropdown .search .search-emoji,
.country-list.wa-phone-dropdown .search .wa-phone-search-emoji {
  flex: 0 0 auto !important;
  font-size: 14px !important;
  line-height: 1 !important;
  opacity: 0.85 !important;
}
.country-list.wa-phone-dropdown .search .search-box,
.country-list.wa-phone-dropdown .search input.search-box,
.country-list.wa-phone-dropdown .search .wa-phone-search-box {
  flex: 1 1 auto !important;
  width: auto !important;
  min-width: 0 !important;
  height: 34px !important;
  margin: 0 !important;
  padding: 6px 10px !important;
  border-radius: 8px !important;
  border: 1px solid #cbd5e1 !important;
  background-color: #ffffff !important;
  color: #0f172a !important;
  outline: none !important;
  box-shadow: none !important;
  box-sizing: border-box !important;
  -webkit-text-fill-color: #0f172a !important;
  caret-color: #0f172a !important;
}
.country-list.wa-phone-dropdown.wa-phone-dropdown--dark .search .search-box,
.country-list.wa-phone-dropdown.wa-phone-dropdown--dark .search input.search-box,
.country-list.wa-phone-dropdown.wa-phone-dropdown--dark .search .wa-phone-search-box {
  background-color: #1e293b !important;
  color: #e2e8f0 !important;
  border: 1px solid #475569 !important;
  -webkit-text-fill-color: #e2e8f0 !important;
  caret-color: #e2e8f0 !important;
}
.country-list.wa-phone-dropdown .search .search-box::placeholder,
.country-list.wa-phone-dropdown.wa-phone-dropdown--dark .search .search-box::placeholder {
  color: #94a3b8 !important;
  opacity: 1 !important;
  -webkit-text-fill-color: #94a3b8 !important;
}
.country-list.wa-phone-dropdown .no-entries-message {
  padding: 12px !important;
  color: #64748b !important;
}
.country-list.wa-phone-dropdown.wa-phone-dropdown--dark .no-entries-message {
  color: #94a3b8 !important;
}
`;

function ensureThemeStyles() {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = THEME_CSS;
  for (let v = 1; v <= 9; v += 1) {
    const id = v === 1
      ? 'whatsapp-phone-field-dropdown-theme'
      : `whatsapp-phone-field-dropdown-theme-v${v}`;
    document.getElementById(id)?.remove();
  }
}

type OverflowBackup = {
  el: HTMLElement;
  overflow: string;
  overflowX: string;
  overflowY: string;
};

function syncOverflowUnlock(root: HTMLElement, backups: OverflowBackup[]) {
  const btn = root.querySelector('.flag-dropdown');
  const open = !!btn?.classList.contains('open');

  if (!open) {
    while (backups.length) {
      const item = backups.pop()!;
      item.el.style.overflow = item.overflow;
      item.el.style.overflowX = item.overflowX;
      item.el.style.overflowY = item.overflowY;
    }
    return;
  }

  if (backups.length) return;

  let el: HTMLElement | null = root.parentElement;
  while (el && el !== document.body) {
    const cs = getComputedStyle(el);
    if (cs.overflow !== 'visible' || cs.overflowX !== 'visible' || cs.overflowY !== 'visible') {
      backups.push({
        el,
        overflow: el.style.overflow,
        overflowX: el.style.overflowX,
        overflowY: el.style.overflowY,
      });
      el.style.overflow = 'visible';
      el.style.overflowX = 'visible';
      el.style.overflowY = 'visible';
    }
    if (cs.position !== 'static' && (cs.zIndex === 'auto' || Number(cs.zIndex) < 1000)) {
      if (!el.dataset.waZBackup) {
        el.dataset.waZBackup = el.style.zIndex || '';
        el.style.zIndex = DROPDOWN_Z;
      }
    }
    el = el.parentElement;
  }
}

function restoreZIndexes(root: HTMLElement) {
  let el: HTMLElement | null = root.parentElement;
  while (el && el !== document.body) {
    if (el.dataset.waZBackup !== undefined) {
      el.style.zIndex = el.dataset.waZBackup;
      delete el.dataset.waZBackup;
    }
    el = el.parentElement;
  }
}

function syncDropdownWidth(root: HTMLElement) {
  const list = root.querySelector('.country-list') as HTMLElement | null;
  if (!list) return;
  const width = Math.max(root.offsetWidth || 0, 280);
  list.style.setProperty('width', `${width}px`, 'important');
  list.style.setProperty('min-width', `${width}px`, 'important');
  list.style.setProperty('max-width', `${width}px`, 'important');
}

/**
 * Web WhatsApp phone entry with country dial-code selector.
 */
const WhatsAppPhoneField = ({
  value,
  onChange,
  disabled,
  placeholder,
  theme = true,
  testID,
}: WhatsAppPhoneFieldProps) => {
  const isDark = !theme;
  const rootRef = useRef<HTMLDivElement>(null);
  const overflowBackupsRef = useRef<OverflowBackup[]>([]);

  useEffect(() => {
    ensureThemeStyles();
    const rootNode = rootRef.current;
    if (!rootNode) return;
    const backups = overflowBackupsRef.current;

    const sync = () => {
      const btn = rootNode.querySelector('.flag-dropdown');
      const open = !!btn?.classList.contains('open');
      if (!open) {
        while (backups.length) {
          const item = backups.pop()!;
          item.el.style.overflow = item.overflow;
          item.el.style.overflowX = item.overflowX;
          item.el.style.overflowY = item.overflowY;
        }
        restoreZIndexes(rootNode);
        return;
      }
      syncOverflowUnlock(rootNode, backups);
      syncDropdownWidth(rootNode);
    };

    const observer = new MutationObserver(() => {
      requestAnimationFrame(sync);
    });
    observer.observe(rootNode, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
    });
    rootNode.addEventListener('click', sync, true);

    return () => {
      observer.disconnect();
      rootNode.removeEventListener('click', sync, true);
      while (backups.length) {
        const item = backups.pop()!;
        item.el.style.overflow = item.overflow;
        item.el.style.overflowX = item.overflowX;
        item.el.style.overflowY = item.overflowY;
      }
      restoreZIndexes(rootNode);
    };
  }, []);

  const colors = useMemo(
    () => ({
      inputBackground: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
      inputBorder: theme ? 'rgba(148, 163, 184, 0.4)' : 'rgba(148, 163, 184, 0.35)',
      inputText: theme ? '#0f172a' : '#e2e8f0',
    }),
    [theme],
  );

  // Single token — library appends `-box` / `-emoji` suffixes to this class
  const searchClass = 'wa-phone-search';
  const dropdownClass = isDark ? 'wa-phone-dropdown wa-phone-dropdown--dark' : 'wa-phone-dropdown';

  return (
    <View style={{ width: '100%', alignSelf: 'stretch' }}>
      <div
        ref={rootRef}
        className={isDark ? 'wa-phone-field wa-phone-field--dark' : 'wa-phone-field'}
        data-testid={testID}
        style={{
          backgroundColor: colors.inputBackground,
          borderColor: colors.inputBorder,
          borderWidth: 1,
          borderStyle: 'solid',
          borderRadius: 12,
          overflow: 'visible',
          zIndex: 20,
          width: '100%',
          minWidth: '100%',
          position: 'relative',
          boxSizing: 'border-box',
        }}
      >
        <PhoneInput
          country={getDefaultPhoneCountryCode()}
          value={value?.replace(/^\+/, '') || ''}
          onChange={(num) => onChange(num ? `+${String(num).replace(/\D/g, '')}` : '')}
          disabled={disabled}
          enableSearch
          countryCodeEditable={false}
          placeholder={placeholder}
          dropdownClass={dropdownClass}
          searchClass={searchClass}
          inputClass="wa-phone-input"
          inputProps={{
            name: 'whatsappPhone',
            required: true,
            autoFocus: false,
          }}
          containerClass="wa-phone-container"
          containerStyle={{
            width: '100%',
            minWidth: '100%',
            backgroundColor: 'transparent',
            borderWidth: 0,
            overflow: 'visible',
          }}
          inputStyle={{
            width: '100%',
            minWidth: '100%',
            height: 50,
            fontSize: responsiveFontSize(15),
            color: colors.inputText,
            backgroundColor: 'transparent',
            borderWidth: 0,
            paddingLeft: 64,
          }}
          buttonStyle={{
            backgroundColor: 'transparent',
            borderWidth: 0,
            width: 52,
          }}
          dropdownStyle={{
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            borderColor: isDark ? '#374151' : '#e2e8f0',
            color: isDark ? '#e2e8f0' : '#0f172a',
            borderRadius: 8,
            maxHeight: 240,
            minWidth: 280,
            left: 0,
            top: 'calc(100% + 4px)',
            position: 'absolute',
            zIndex: Number(DROPDOWN_Z),
          }}
          searchStyle={{
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            color: isDark ? '#e2e8f0' : '#0f172a',
            border: isDark ? '1px solid #475569' : '1px solid #cbd5e1',
            borderRadius: 8,
            margin: 0,
            outline: 'none',
            width: '100%',
          }}
        />
      </div>
    </View>
  );
};

export default WhatsAppPhoneField;
